import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchRunLogs } from '../../services/activeRunsService';
import axios from '../../services/authService';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { rerunScenario } from '../../services/appFlowsService';
import { parseBehaveLogs } from '../../utilities/logs/parseBehaveLogs';

export default function useRunCard({ run, showLogs }) {
  const navigate = useNavigate();
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [screenshotExpanded, setScreenshotExpanded] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [screenshotError, setScreenshotError] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [logs, setLogs] = useState([]);
  const [isFetchingLogs, setIsFetchingLogs] = useState(false);
  const [logsError, setLogsError] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);

  const handleRerun = async () => {
    try {
      setIsRerunning(true);
      await rerunScenario(run);
    } catch {
    } finally {
      setIsRerunning(false);
    }
  };

  // Tick timer for running runs
  useEffect(() => {
    if (run.status !== 'running') return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [run.status]);

  // Fetch + poll logs
  useEffect(() => {
    if (!showLogs && !logsExpanded) return;

    let mounted = true;

    async function loadLogs() {
      try {
        setIsFetchingLogs(true);
        const res = await fetchRunLogs(run.runId, 0);
        if (mounted) setLogs(res.logs || []);
        setLogsError(false);
      } catch {
        setLogsError(true);
      } finally {
        setIsFetchingLogs(false);
      }
    }

    loadLogs();

    let id;
    if (run.status === 'running') {
      id = setInterval(loadLogs, 1000);
    }

    return () => {
      mounted = false;
      if (id) clearInterval(id);
    };
  }, [run.runId, run.status, showLogs, logsExpanded]);

  // Load screenshot on demand when expanded
  useEffect(() => {
    if (!screenshotExpanded || run.status !== 'failed') return;
    if (screenshotUrl) return; // already loaded

    const url = API_ENDPOINTS.GET_RUN_SCREENSHOT(run.runId);
    axios
      .get(url, { responseType: 'blob' })
      .then((res) => {
        if (res.headers['content-type']?.includes('image')) {
          setScreenshotUrl(URL.createObjectURL(res.data));
        } else {
          setScreenshotError(true);
        }
      })
      .catch(() => setScreenshotError(true));
  }, [screenshotExpanded, run.runId, run.status, screenshotUrl]);

  const toUtcDate = (iso) => {
    if (!iso) return null;
    return /[zZ]|[+-]\d{2}:\d{2}$/.test(iso) ? new Date(iso) : new Date(`${iso}Z`);
  };

  const formatLocalHHmm = (iso) => {
    const d = toUtcDate(iso);
    if (!d) return '--';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatLocalHHmmRoundedUp = (iso) => {
    const d = toUtcDate(iso);
    if (!d) return '--';
    const roundedUpMs = Math.ceil(d.getTime() / 60000) * 60000;
    return new Date(roundedUpMs).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const formatLocalDayMonth = (iso) => {
    const d = toUtcDate(iso);
    if (!d) return '--';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  };

  const elapsedTime = useMemo(() => {
    const start = new Date(run.startedAt || run.createdAt).getTime();
    const end = run.finishedAt
      ? new Date(run.finishedAt).getTime()
      : run.status === 'running'
        ? nowMs
        : Date.now();

    const seconds = Math.floor((end - start) / 1000);
    return seconds >= 60 ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : `${seconds}s`;
  }, [run.createdAt, run.startedAt, run.finishedAt, run.status, nowMs]);

  const startedTime = useMemo(() => {
    return formatLocalHHmm(run.startedAt || run.createdAt);
  }, [run.createdAt, run.startedAt]);

  const startedDate = useMemo(() => {
    return formatLocalDayMonth(run.startedAt || run.createdAt);
  }, [run.createdAt, run.startedAt]);

  const finishedTime = useMemo(() => {
    return formatLocalHHmmRoundedUp(run.finishedAt);
  }, [run.finishedAt]);

  const finishedDate = useMemo(() => {
    return formatLocalDayMonth(run.finishedAt);
  }, [run.finishedAt]);

  const datesMatch = useMemo(() => {
    if (!run.finishedAt) return true;
    return startedDate === finishedDate;
  }, [run.finishedAt, startedDate, finishedDate]);

  const statusColor = {
    running: 'warning',
    queued: 'info',
    completed: 'success',
    failed: 'error',
    cancelled: 'default',
  }[run.status];

  const parsedFeatures = useMemo(() => {
    if (!showLogs) return [];
    if (!logs?.length) return [];
    return parseBehaveLogs(logs);
  }, [showLogs, logs]);

  const handleNavigateToEvents = () => {
    navigate('/', {
      state: {
        searchedRun: run,
      },
    });
  };

  return {
    datesMatch,
    elapsedTime,
    finishedDate,
    finishedTime,
    handleNavigateToEvents,
    handleRerun,
    isFetchingLogs,
    isRerunning,
    logs,
    logsError,
    logsExpanded,
    parsedFeatures,
    screenshotError,
    screenshotExpanded,
    screenshotUrl,
    setLogsExpanded,
    setScreenshotExpanded,
    startedDate,
    startedTime,
    statusColor,
  };
}
